import {
  buildNormalizedDay,
  buildNormalizedPilotBlueprint,
  localizedGenderNode,
} from "./referenceNormalizerPilotUtils";

/** Required substring for scripts/validate_dna_blueprints.mjs (runtime writing steps). */
export const DNA_VALIDATE_WRITING_PHRASE = "אני שואל/ת את עצמי בשקט";

const days = [
  {
    dayNumber: 1,
    emotionalStage: "pause",
    title: "Naming It (Without Arguing With It)",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I don’t try to prove I’m enough. I simply notice the sentence that keeps returning — and stay with myself."
    ],
      [
      "Daily intention: Today I don’t try to prove I’m enough. I simply notice the sentence that keeps returning — and stay with myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One slow breath.",
      "In my heart: “This is the ‘not enough’ voice. It is not me.”"
    ],
      [
      "I pause with myself.",
      "One slow breath.",
      "In my heart: “This is the ‘not enough’ voice. It is not me.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What is the exact sentence that comes up today? (“I’m not enough because…”)",
      "Where do I feel it in my body? (chest / belly / throat / head)",
      "When is it strongest? (morning / work / relationships / friends / social media)"
    ],
      [
      "What is the exact sentence that comes up today? (“I’m not enough because…”)",
      "Where do I feel it in my body? (chest / belly / throat / head)",
      "When is it strongest? (morning / work / relationships / friends / social media)"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Today I don’t defeat the voice. I simply shine a light on it."
    ],
      [
      "Closing",
      "Today I don’t defeat the voice. I simply shine a light on it."
    ]
    ),
    action: localizedGenderNode(
      "Once today: one breath + the sentence, “This is a voice. Not a fact.”",
      "Once today: one breath + the sentence, “This is a voice. Not a fact.”"
    ),
  },
  {
    dayNumber: 2,
    emotionalStage: "pain",
    title: "Fact vs. Interpretation (Gentle CBT)",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I separate what happened from what my mind says it means about me."
    ],
      [
      "Daily intention: Today I separate what happened from what my mind says it means about me."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “Truth doesn’t have to hurt like this.”"
    ],
      [
      "I pause with myself.",
      "One breath. “Truth doesn’t have to hurt like this.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What is the bare fact that happened? (2 lines, no interpretation)",
      "What interpretation jumped in immediately? (“It means I am…”)",
      "What is one gentle alternative truth that is also possible?"
    ],
      [
      "What is the bare fact that happened? (2 lines, no interpretation)",
      "What interpretation jumped in immediately? (“It means I am…”)",
      "What is one gentle alternative truth that is also possible?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Not every interpretation is truth. Sometimes it’s fear trying to protect me."
    ],
      [
      "Closing",
      "Not every interpretation is truth. Sometimes it’s fear trying to protect me."
    ]
    ),
    action: localizedGenderNode(
      "Once today: “Fact ≠ interpretation.” Then one breath.",
      "Once today: “Fact ≠ interpretation.” Then one breath."
    ),
  },
  {
    dayNumber: 3,
    emotionalStage: "pain",
    title: "The Inner Critic (Separating “Me” from “The Critic”)",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I notice who is speaking: me — or the inner critic."
    ],
      [
      "Daily intention: Today I notice who is speaking: me — or the inner critic."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “A critic is a mechanism, not an identity.”"
    ],
      [
      "I pause with myself.",
      "One breath. “A critic is a mechanism, not an identity.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What does the critic say to me today?",
      "If the critic were a character — how would it look or sound?",
      "What would I say to a loved friend in my place?"
    ],
      [
      "What does the critic say to me today?",
      "If the critic were a character — how would it look or sound?",
      "What would I say to a loved friend in my place?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I don’t have to speak to myself in a voice that weakens me."
    ],
      [
      "Closing",
      "I don’t have to speak to myself in a voice that weakens me."
    ]
    ),
    action: localizedGenderNode(
      "When the critic appears: “Hello, critic. I hear you.” Then I return to my breath.",
      "When the critic appears: “Hello, critic. I hear you.” Then I return to my breath."
    ),
  },
  {
    dayNumber: 4,
    emotionalStage: "pattern",
    title: "Where I Learned “Not Enough” (A Gentle Root, No Digging)",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I see where this began — so it stops feeling like absolute truth."
    ],
      [
      "Daily intention: Today I see where this began — so it stops feeling like absolute truth."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “What I learned once doesn’t have to run my life today.”"
    ],
      [
      "I pause with myself.",
      "One breath. “What I learned once doesn’t have to run my life today.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "When did I first feel “not enough,” as far as I can remember?",
      "What did I want back then that I didn’t receive? (approval / warmth / being seen / protection)",
      "What sentence would I like to say to my younger self today?"
    ],
      [
      "When did I first feel “not enough,” as far as I can remember?",
      "What did I want back then that I didn’t receive? (approval / warmth / being seen / protection)",
      "What sentence would I like to say to my younger self today?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "The pain is real — but it is not proof that I am less."
    ],
      [
      "Closing",
      "The pain is real — but it is not proof that I am less."
    ]
    ),
    action: localizedGenderNode(
      "One sentence to myself: “I was a child. I did the best I could.”",
      "One sentence to myself: “I was a child. I did the best I could.”"
    ),
  },
  {
    dayNumber: 5,
    emotionalStage: "pattern",
    title: "Comparison to Others (Bringing the Measure Back Home)",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I stop measuring myself against others."
    ],
      [
      "Daily intention: Today I stop measuring myself against others."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “Comparison is pain disguised as truth.”"
    ],
      [
      "I pause with myself.",
      "One breath. “Comparison is pain disguised as truth.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Who have I been comparing myself to lately, and in what area?",
      "What do I tell myself this comparison says about me?",
      "What is one true thing about me that doesn’t fit into this comparison?"
    ],
      [
      "Who have I been comparing myself to lately, and in what area?",
      "What do I tell myself this comparison says about me?",
      "What is one true thing about me that doesn’t fit into this comparison?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I wasn’t born to beat others. I was born to be me."
    ],
      [
      "Closing",
      "I wasn’t born to beat others. I was born to be me."
    ]
    ),
    action: localizedGenderNode(
      "Once today I do a “comparison stop”: breath + “back to myself.”",
      "Once today I do a “comparison stop”: breath + “back to myself.”"
    ),
  },
  {
    dayNumber: 6,
    emotionalStage: "fear",
    title: "Receiving a Compliment Without Running",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I practice receiving goodness without shrinking."
    ],
      [
      "Daily intention: Today I practice receiving goodness without shrinking."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “I’m allowed to receive.”"
    ],
      [
      "I pause with myself.",
      "One breath. “I’m allowed to receive.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "How do I usually respond to compliments? (dismiss / joke / deflect / change subject)",
      "What scares me about receiving a compliment?",
      "What is one simple, healthy response? (e.g., “Thank you”)"
    ],
      [
      "How do I usually respond to compliments? (dismiss / joke / deflect / change subject)",
      "What scares me about receiving a compliment?",
      "What is one simple, healthy response? (e.g., “Thank you”)"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Receiving a compliment isn’t arrogance. It’s self-respect."
    ],
      [
      "Closing",
      "Receiving a compliment isn’t arrogance. It’s self-respect."
    ]
    ),
    action: localizedGenderNode(
      "If someone says something good — I reply only: “Thank you.”",
      "If someone says something good — I reply only: “Thank you.”"
    ),
  },
  {
    dayNumber: 7,
    emotionalStage: "need",
    title: "A Small Experiment (Behavior): Acting Despite the Voice",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I do something small even if the voice says “not enough.”"
    ],
      [
      "Daily intention: Today I do something small even if the voice says “not enough.”"
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “I don’t need to feel enough to act.”"
    ],
      [
      "I pause with myself.",
      "One breath. “I don’t need to feel enough to act.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What small action have I been avoiding because of “not enough”?",
      "What is a 10% version of that action?",
      "What do I want to prove to myself through this 10%?"
    ],
      [
      "What small action have I been avoiding because of “not enough”?",
      "What is a 10% version of that action?",
      "What do I want to prove to myself through this 10%?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "My worth doesn’t depend on a perfect outcome — it depends on loyalty to myself."
    ],
      [
      "Closing",
      "My worth doesn’t depend on a perfect outcome — it depends on loyalty to myself."
    ]
    ),
    action: localizedGenderNode(
      "I do my 10%. That’s it.",
      "I do my 10%. That’s it."
    ),
  },
  {
    dayNumber: 8,
    emotionalStage: "softening",
    title: "Allowed Discomfort (ACT): Carrying “Less Than” for One Minute",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I practice staying with the feeling for one minute — without fixing it."
    ],
      [
      "Daily intention: Today I practice staying with the feeling for one minute — without fixing it."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “A feeling is a wave, not a verdict.”"
    ],
      [
      "I pause with myself.",
      "One breath. “A feeling is a wave, not a verdict.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What is the strongest trigger for “I’m not enough”?",
      "One-minute practice: I let the feeling be here — without action.\nWhat happened in my body after the minute?",
      "What did I learn about the wave?"
    ],
      [
      "What is the strongest trigger for “I’m not enough”?",
      "One-minute practice: I let the feeling be here — without action.\nWhat happened in my body after the minute?",
      "What did I learn about the wave?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I grow stronger not because the feeling disappears — but because I stay."
    ],
      [
      "Closing",
      "I grow stronger not because the feeling disappears — but because I stay."
    ]
    ),
    action: localizedGenderNode(
      "One minute only. Consistency > force.",
      "One minute only. Consistency > force."
    ),
  },
  {
    dayNumber: 9,
    emotionalStage: "softening",
    title: "Self-Worth vs. Performance",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I separate who I am from how it went."
    ],
      [
      "Daily intention: Today I separate who I am from how it went."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “Failure is an event, not an identity.”"
    ],
      [
      "I pause with myself.",
      "One breath. “Failure is an event, not an identity.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Where do I confuse performance with self-worth?",
      "What would I say if this were just an attempt, not a test?",
      "What is one thing I do well, even if not perfectly?"
    ],
      [
      "Where do I confuse performance with self-worth?",
      "What would I say if this were just an attempt, not a test?",
      "What is one thing I do well, even if not perfectly?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I can improve without shrinking myself."
    ],
      [
      "Closing",
      "I can improve without shrinking myself."
    ]
    ),
    action: localizedGenderNode(
      "Once today I say: “This is a process, not proof.”",
      "Once today I say: “This is a process, not proof.”"
    ),
  },
  {
    dayNumber: 10,
    emotionalStage: "perspective",
    title: "Being “Enough” Without Proving (Self-Compassion)",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I hold myself the way I’d hold someone I love."
    ],
      [
      "Daily intention: Today I hold myself the way I’d hold someone I love."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “I speak to myself gently.”"
    ],
      [
      "I pause with myself.",
      "One breath. “I speak to myself gently.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What would I say to a close friend who feels “not enough”?",
      "Which one sentence from that can I say to myself today?",
      "Where is it hardest for me to be gentle with myself?"
    ],
      [
      "What would I say to a close friend who feels “not enough”?",
      "Which one sentence from that can I say to myself today?",
      "Where is it hardest for me to be gentle with myself?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Compassion doesn’t make me weak. It gives me strength back."
    ],
      [
      "Closing",
      "Compassion doesn’t make me weak. It gives me strength back."
    ]
    ),
    action: localizedGenderNode(
      "Hand on chest or belly (if comfortable) + my gentle sentence once.",
      "Hand on chest or belly (if comfortable) + my gentle sentence once."
    ),
  },
  {
    dayNumber: 11,
    emotionalStage: "strength",
    title: "Boundaries With External Voices (“What Will They Say”)",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I reduce the external noise that runs me."
    ],
      [
      "Daily intention: Today I reduce the external noise that runs me."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “I don’t need approval to exist.”"
    ],
      [
      "I pause with myself.",
      "One breath. “I don’t need approval to exist.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Where does “what will they say” control me most?",
      "What is the cost of living for approval?",
      "What small boundary will I set this week?"
    ],
      [
      "Where does “what will they say” control me most?",
      "What is the cost of living for approval?",
      "What small boundary will I set this week?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I can listen to others without abandoning myself."
    ],
      [
      "Closing",
      "I can listen to others without abandoning myself."
    ]
    ),
    action: localizedGenderNode(
      "Once today I choose one action just for me.",
      "Once today I choose one action just for me."
    ),
  },
  {
    dayNumber: 12,
    emotionalStage: "release",
    title: "Repairing the Language (Words Build Reality)",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I adjust one inner tone — and it shifts direction."
    ],
      [
      "Daily intention: Today I adjust one inner tone — and it shifts direction."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “Words are a home.”"
    ],
      [
      "I pause with myself.",
      "One breath. “Words are a home.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What word do I label myself with?",
      "How does the same truth sound in human, non-violent language?",
      "What sentence do I adopt for this week?"
    ],
      [
      "What word do I label myself with?",
      "How does the same truth sound in human, non-violent language?",
      "What sentence do I adopt for this week?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "I don’t have to speak to myself in a language of war."
    ],
      [
      "Closing",
      "I don’t have to speak to myself in a language of war."
    ]
    ),
    action: localizedGenderNode(
      "Each time I catch a harsh word — I replace it once.",
      "Each time I catch a harsh word — I replace it once."
    ),
  },
  {
    dayNumber: 13,
    emotionalStage: "choice",
    title: "“Enough” as a Path (Not a Destination)",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I build an identity of movement, not proof."
    ],
      [
      "Daily intention: Today I build an identity of movement, not proof."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “I am on the way.”"
    ],
      [
      "I pause with myself.",
      "One breath. “I am on the way.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "Which value do I want to lead me?",
      "What small habit supports this value this week?",
      "What do I promise myself on hard days?"
    ],
      [
      "Which value do I want to lead me?",
      "What small habit supports this value this week?",
      "What do I promise myself on hard days?"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Closing",
      "Enough is not something I earn. It’s something I practice living from."
    ],
      [
      "Closing",
      "Enough is not something I earn. It’s something I practice living from."
    ]
    ),
    action: localizedGenderNode(
      "I plan one small stabilizing action for the week.",
      "I plan one small stabilizing action for the week."
    ),
  },
  {
    dayNumber: 14,
    emotionalStage: "continuation",
    title: "Gentle Closure (A Return Sentence for Hard Days)",
    opening: localizedGenderNode(
      [
      "Daily intention: Today I finish without “winning.” I finish loyal to myself."
    ],
      [
      "Daily intention: Today I finish without “winning.” I finish loyal to myself."
    ]
    ),
    mindfulness: localizedGenderNode(
      [
      "I pause with myself.",
      "One breath. “I stay with me.”"
    ],
      [
      "I pause with myself.",
      "One breath. “I stay with me.”"
    ]
    ),
    prompts: localizedGenderNode(
      [
      "What changed in me, even slightly? (3 things)",
      "What became clearer about the “not enough” voice?",
      "A return sentence for hard days:"
    ],
      [
      "What changed in me, even slightly? (3 things)",
      "What became clearer about the “not enough” voice?",
      "A return sentence for hard days:"
    ]
    ),
    closing: localizedGenderNode(
      [
      "Final closure of the journey",
      "I don’t erase the voice.",
      "I simply stop letting it run my life.",
      "I keep walking —",
      "slowly, quietly, with myself.",
      "And that is strength."
    ],
      [
      "Final closure of the journey",
      "I don’t erase the voice.",
      "I simply stop letting it run my life.",
      "I keep walking —",
      "slowly, quietly, with myself.",
      "And that is strength."
    ]
    ),
    action: localizedGenderNode(
      "One action that says “I’m on my side.”",
      "One action that says “I’m on my side.”"
    ),
  },
].map(buildNormalizedDay);

export const iAmNotEnoughBlueprintEn = buildNormalizedPilotBlueprint({
  id: "i_am_not_enough_14d",
  version: "tri_locale_emit_en_v1",
  durationDays: 14,
  problemKey: "i_am_not_enough",
  category: "general",
  journeyName: {
    he: "תחושת “אני לא מספיק/ה”",
    en: "I’m Not Enough",
    es: "No soy suficiente",
  },
  days,
});

export default iAmNotEnoughBlueprintEn;
